import { Grid, Typography } from '@methanesat/ui-components';
import { useDispatch, useSelector } from 'react-redux';
import { OGI_NEAR_PLUME_RADIUS_M } from '../../../../../consts';
import { useTranslate } from '../../../../../hooks';
import { selectPlatform, setOgiNearby, useGetOgiMetadataQuery, useGetOgiNearPointQuery } from '../../../../../reducers';
import {
    AreaEmissionDrawerProps,
    OGILayerIds,
    OGISummary,
    PipelineFeatureProperties,
    PlumeDrawerProps,
    PointOGIMGeoJSONProperties,
    RawOGIData,
    SourceIds
} from '../../../../../types';
import { analytics } from '../../../../../utils';
import { OGINearEmissionsSkeleton } from '../../Layout/OGINearEmissionsSkeleton';
import { OGIDetails } from './OGIDetails';
import { OGIDetailsExitButton } from './OGIDetailsExitButton';
import { OGIDownload } from './OGIDownload';
import { OGIMoreInfoButton } from './OGIMoreInfoButton';
import { OGISummaryBars } from './OGISummaryBars';

/** Ingests list of OGI features & returns a summary by type */
const getOgiSummary = (ogiList?: RawOGIData[]): OGISummary => {
    return ogiList
        ? ogiList
              .map((feature) => feature.layer_name)
              .reduce(
                  (acc: { [key in OGILayerIds]: number }, curr: OGILayerIds) => {
                      if (curr in acc) {
                          acc[curr]++;
                      } else {
                          acc[curr] = 1;
                      }
                      return acc;
                  },
                  {} as { [key in OGILayerIds]: number }
              )
        : {};
};

/** Gets OGI data for  */
export function useGetOgiNearPointDrawer({ longitude, latitude }: { longitude: number; latitude: number }) {
    const response = useGetOgiNearPointQuery({ longitude, latitude });
    const ogiList: RawOGIData[] = response.data;

    const ogiSummary = getOgiSummary(ogiList);

    // TODO: fetch meatadata only when necessary (i.e., when user requests ogi details)

    // Get additional metadata for the OGIM data near point
    const ogimIdsToFetch = ogiList?.map(({ ogim_id }: { ogim_id: number }) => ogim_id);

    const { data: ogiNearPointWithMetadataList, isFetching: fetchingData } = useGetOgiMetadataQuery(
        { ogimIds: ogimIdsToFetch },
        { skip: !ogimIdsToFetch || !ogimIdsToFetch.length }
    );

    const ogiNearPointWithMetadata = ogiNearPointWithMetadataList as (
        | PipelineFeatureProperties
        | PointOGIMGeoJSONProperties
    )[];

    // Get the layer types of the OGIM data that is near point indexed by the ogim id
    const ogiTypesNearPoint = ogiList?.reduce<{ [key: string]: OGILayerIds }>((accum, { ogim_id, layer_name }) => {
        accum[ogim_id] = layer_name;
        return accum;
    }, {});

    // Add the layer type of ogim data to the remaining metadata
    const ogiNearPointWithMetadataLayerId =
        ogiTypesNearPoint && ogiNearPointWithMetadata && !fetchingData
            ? ogiNearPointWithMetadata.map((ogi: PipelineFeatureProperties | PointOGIMGeoJSONProperties) => ({
                  ...ogi,
                  layerName: ogiTypesNearPoint[ogi.ogim_id]
              }))
            : ogiList === undefined
              ? undefined // return undefined to signal data is being fetched
              : []; // otherwise, return an empty list to signal no data

    return { ogiSummary, ogiDetailsList: ogiNearPointWithMetadataLayerId };
}

export const OGINearEmissions = ({
    info,
    emissionRate,
    longitude,
    latitude,
    sourceType,
    showDetails,
    resetMap
}: {
    info: PlumeDrawerProps['info'] | AreaEmissionDrawerProps['info'];
    emissionRate: number;
    longitude: number;
    latitude: number;
    sourceType: SourceIds;
    showDetails: boolean;
    resetMap: () => void;
}) => {
    const t = useTranslate();
    const dispatch = useDispatch();
    const platform = useSelector(selectPlatform);
    const { ogiSummary, ogiDetailsList } = useGetOgiNearPointDrawer({ longitude, latitude });
    const ogiCount = Object.values(ogiSummary).reduce((acc, curr) => (acc += curr), 0);

    const handleShowOgiDetails = () => {
        if (longitude !== undefined && latitude !== undefined && ogiDetailsList && !!emissionRate) {
            // We add 0.01 degree to the longitude to make the map look centered when the drawer is open
            // TODO: handle this dynamically in the future
            dispatch(
                setOgiNearby({
                    longitude: longitude + 0.01,
                    latitude,
                    sourceType: sourceType,
                    ogimFeatures: ogiDetailsList
                })
            );
            if (sourceType == SourceIds.distinct) {
                analytics.showOgiNearPlumes({
                    id: `${info.object.id}`,
                    count: ogiDetailsList.length,
                    emissionRate: emissionRate,
                    latitude: info.coordinate?.[1],
                    longitude: info.coordinate?.[0]
                });
            } else {
                analytics.showOgiNearRasterPoints({
                    id: `${info.object.id}`,
                    count: ogiDetailsList.length,
                    emissionRate: emissionRate,
                    latitude: info.coordinate?.[1],
                    longitude: info.coordinate?.[0]
                });
            }
        }
    };

    // show skeleton loader while data is fetched
    if (!showDetails && !ogiDetailsList) {
        return <OGINearEmissionsSkeleton />;
    }

    return (
        <Grid container marginTop={-2}>
            {/* summary bars */}
            {!showDetails && (
                // Show point (plume or raster) metadata & OGI summary
                <Grid size={{ xs: 12 }}>
                    <OGISummaryBars summary={ogiSummary} total={ogiCount} />
                </Grid>
            )}

            {/* Show OGI details */}
            {showDetails && ogiDetailsList && (
                <Grid size={{ xs: 12 }} marginTop={4}>
                    <OGIDetails features={ogiDetailsList} />
                </Grid>
            )}

            {/* disclaimer */}
            {showDetails && ogiDetailsList && (
                <Grid size={{ xs: 12 }} marginTop={4}>
                    <Typography sx={{ marginBottom: 2 }}>
                        {t('emissionsMapPage.infrastructure.ogiNearEmissions.common.nearbyFacilitiesDistanceNote', {
                            radiusMeters: OGI_NEAR_PLUME_RADIUS_M,
                            sourceType: t('emissionsMapPage.infrastructure.ogiNearEmissions.sourceType.distinctPoint')
                        })}
                    </Typography>
                    <Typography>
                        {t('emissionsMapPage.infrastructure.ogiNearEmissions.common.uncertaintyNote')}
                    </Typography>
                </Grid>
            )}

            {/* Conditional buttons */}
            {showDetails && ogiDetailsList && (
                <Grid size={{ xs: 12 }} display="flex" justifyContent="center" marginTop={5}>
                    <OGIDownload
                        disabled={ogiDetailsList?.length === 0}
                        coordinates={[latitude, longitude]}
                        details={ogiDetailsList}
                        trackDownload={() =>
                            analytics.downloadOgiNearby({
                                latitude,
                                longitude,
                                sourceType,
                                platform,
                                ogiCount,
                                emissionRate
                            })
                        }
                    />
                </Grid>
            )}

            {/* Button to view ogi details. It is very important that ogiDetailsList is truthy. Otherwise button won't work */}
            {!showDetails && ogiDetailsList && (
                <Grid size={{ xs: 12 }} display="flex" justifyContent="center" marginTop={4} marginBottom={2}>
                    <OGIMoreInfoButton
                        disabled={ogiDetailsList?.length === 0}
                        onClick={handleShowOgiDetails}
                        data-testid="ogi-near-emissions-prompt-button"
                    />
                </Grid>
            )}

            {/* Button to return to main drawer view */}
            {showDetails && (
                <Grid size={{ xs: 12 }} display="flex" justifyContent="center" marginTop={4} marginBottom={3}>
                    <OGIDetailsExitButton
                        data-testid="ogi-near-point-exit-button"
                        onClick={resetMap}
                        text={t('emissionsMapPage.infrastructure.ogiNearEmissions.common.returnToEmissions')}
                    />
                </Grid>
            )}
        </Grid>
    );
};
